import { useCallback } from 'react';
import {
  useMatchRoute,
  useNavigate,
  useParams,
  useSearch,
} from '@tanstack/react-router';

import { useQueryClient } from '@tanstack/react-query';

import { useAiToolHelp } from '@/features/editor-access';
import { useCurrentUser, useIsOrgAdmin } from '@/features/identity';
import { env } from '@/lib/env';
import {
  standingOf,
  useWorkspaceList,
  workspaceKeys,
  workspaceMarkWords,
  workspaceMarks,
} from '@/features/workspaces';
import {
  canRunAgain,
  derivePrimaryAction,
  hasLiveExecution,
  notStartedCount,
  STOP_WAITING,
  taskPlacement,
  useTaskCommandChannel,
  waitingOnChildren,
  type BoardSearch,
} from '@/features/tasks';
import type { WorkspaceAggregate } from '@/lib/api/generated/model';
import { useThemeStore } from '@/shared/store/theme';
import type { ActionId, EntrySources, OpenTaskActions } from '../entries';
import { nextTaskActions, nextTaskCommand } from '../taskActions';
import { useCommandPalette, useShortcutSheet } from '../useCommandPalette';
import { CommandPalette } from './CommandPalette';
import { ShortcutSheet } from './ShortcutSheet';

/**
 * Mounts the palette and the shortcut sheet, and gathers what they need.
 *
 * **Mounted at the root route, not in `ShellFrame`.** The shell serves the workspace and
 * the console — but the workspace *list* has no shell at all: it is the front door, it
 * renders its own header, and spec 075 records that as deliberate. Mounting in the shell
 * would leave the palette unavailable on the one page whose whole purpose is choosing a
 * workspace, which is the most likely place anyone reaches for ⌘K.
 *
 * Everything here is read from queries that are already running. Nothing is fetched for
 * the palette's sake (spec 077 FR-024), which is why there is no `enabled` flag keyed on
 * whether it is open: these hooks are subscriptions to caches the application already
 * fills.
 */
const TASK_COMMAND = {
  'task-start-children': 'start-children',
  'task-stop': 'stop',
  'task-run-again': 'run-again',
  'task-hide': 'hide',
  'task-copy-link': 'copy-link',
} as const;

export function CommandPaletteHost() {
  const palette = useCommandPalette();
  const sheet = useShortcutSheet();
  const navigate = useNavigate();
  const setTheme = useThemeStore((state) => state.setTheme);
  const theme = useThemeStore((state) => state.theme);

  // `undefined` outside a workspace route, which is exactly the distinction FR-012 rests
  // on — and is why it is read from the address rather than guessed.
  const { workspaceId } = useParams({ strict: false });
  // Which board, if the reader is on one. Matched against the route rather than
  // read as a bare `workflowKey` param, because the builder carries that param
  // too and a draft on the builder has no board to create a task on.
  const matchRoute = useMatchRoute();
  const boardMatch = matchRoute({
    to: '/workspaces/$workspaceId/workflows/$workflowKey/board',
  });
  const boardWorkflowKey = boardMatch ? boardMatch.workflowKey : undefined;

  const { data: currentUser } = useCurrentUser();
  const isOrgAdmin = useIsOrgAdmin();
  /*
    Fetched when the palette is first opened, never on page load.

    FR-024 says the palette causes no request, and the first version of this broke it:
    an unconditional `useWorkspaceList` added a request to *every* page in the
    application, which two existing guard tests caught — "fetches nothing beyond the
    shell reading its own workspace" and "renders every entry from a single request".
    They were right and the code was wrong.

    Gating on `open` keeps the requirement's intent, which is that the palette must not
    make every page load heavier. A request caused by a reader deliberately opening a
    search is not the thing FR-024 was protecting against — and the alternative, reading
    only what happens to be cached, would leave the palette unable to find a workspace
    for anyone who had not visited the list this session, which is Story 1's whole point.
  */
  const workspaceList = useWorkspaceList({ enabled: palette.open, limit: 100 });

  /*
    Read from the cache rather than subscribed to.

    `useWorkspace` has no `enabled`, so calling it outside a workspace would mean issuing
    a request for an empty id — and FR-024 is explicit that the palette causes no request
    of its own. Reading what is already there is also the honest semantic: the palette
    searches what the application holds, and `contentsSearchable` is what tells the reader
    when that is nothing.

    Not reactive, deliberately. The palette is opened on demand and reads the cache at
    that moment; a subscription would buy a re-render nobody is watching.
  */
  const queryClient = useQueryClient();
  const workspace =
    workspaceId === undefined
      ? undefined
      : queryClient.getQueryData<WorkspaceAggregate>(
          workspaceKeys.detail(workspaceId),
        );

  /*
    The task whose drawer is open — the board and the Tasks page both carry it
    as `?task=` — and what it can do now (spec 125 FR-014). Read from the same
    cached aggregate as everything else here, so it adds no request.
  */
  const search: { task?: unknown } = useSearch({ strict: false });
  const openTaskId = typeof search.task === 'string' ? search.task : undefined;
  const requestTaskCommand = useTaskCommandChannel((state) => state.request);
  const openTask: OpenTaskActions | undefined = (() => {
    if (!openTaskId || !workspace) return undefined;
    const placement = taskPlacement(
      workspace.workflows,
      workspace.tasks,
      openTaskId,
    );
    const task = placement.task;
    if (!task) return undefined;
    const action = derivePrimaryAction(
      task,
      placement.location?.step,
      placement.steps,
    );
    return {
      id: task.id,
      next: nextTaskActions(action),
      isRunning: hasLiveExecution(task),
      stopLabel: waitingOnChildren(task) !== null ? STOP_WAITING : undefined,
      waitingToStart: notStartedCount(task.linked_tasks),
      canRunAgain: canRunAgain(task.executions ?? []),
      atGate: placement.location?.step?.kind === 'human_gate',
      isHidden: task.hidden_at != null,
    };
  })();

  // Whether the AI tools page is worth offering from anywhere (spec 248), asked
  // once the palette is open, as the workspace list above is.
  const aiHelp = useAiToolHelp({ enabled: palette.open });

  // The sidebar's rule, from the same cached read (spec 188).
  const standing = standingOf(workspace, currentUser?.id, isOrgAdmin);

  const sources: EntrySources = {
    mcpOn: aiHelp !== 'hidden',
    viewer: {
      isWorkspaceOwner: standing.isOwner,
      isOrgAdmin,
      isWorkspaceMember: !standing.readOnly,
      showUnbuilt: env.VITE_SHOW_UNBUILT_SECTIONS,
    },
    workspaces: (workspaceList.data?.items ?? []).map((entry) => ({
      id: entry.id,
      name: entry.name,
      marks: workspaceMarkWords(workspaceMarks(entry, currentUser?.id)),
    })),
    // Present only when the aggregate has actually resolved. A workspace whose contents
    // have not arrived is not searchable, and saying so is the point.
    current:
      workspaceId !== undefined && workspace
        ? {
            id: workspaceId,
            workflows: workspace.workflows,
            tasks: workspace.tasks,
            boardWorkflowKey,
            openTask,
          }
        : undefined,
  };

  const onAction = useCallback(
    (action: ActionId, arg?: string) => {
      // Each action runs what the equivalent control runs (FR-015). Where a dialog is
      // involved the palette navigates to the surface that owns it rather than opening
      // a second copy — one dialog, one owner.
      switch (action) {
        case 'new-task':
          // To the board's own dialog, by the parameter its schema declares.
          // This used to go to the task list with a `create` flag that page
          // never declared, cast `as never` — the cast is what let a dead
          // action type-check.
          if (workspaceId !== undefined && boardWorkflowKey !== undefined) {
            void navigate({
              to: '/workspaces/$workspaceId/workflows/$workflowKey/board',
              params: { workspaceId, workflowKey: boardWorkflowKey },
              // The reader's filters stay; only the request is added. The
              // parameter is typed against the board's own schema because a
              // route-free `navigate` sees every route's search at once.
              search: (prev) => ({ ...(prev as BoardSearch), create: true }),
            });
          }
          return;
        case 'new-workspace':
          // With `create`, not bare. Navigating alone left the reader on a page
          // they could already reach, with nothing to show the action had run.
          void navigate({ to: '/', search: { create: true } });
          return;
        case 'toggle-theme':
          setTheme(theme === 'dark' ? 'light' : 'dark');
          return;
        // The workspace's create verbs (spec 129 FR-009): to the page, with
        // the request its form honours.
        case 'add-execution-image':
          // A Settings tab since spec 224.
          if (workspaceId === undefined) return;
          void navigate({
            to: '/workspaces/$workspaceId/settings/images',
            params: { workspaceId },
            search: { create: true },
          });
          return;
        case 'add-secret':
        case 'add-connection':
        case 'new-trigger': {
          if (workspaceId === undefined) return;
          const to = {
            'add-secret': '/workspaces/$workspaceId/integrations/secrets',
            'add-connection':
              '/workspaces/$workspaceId/integrations/connections',
            'new-trigger': '/workspaces/$workspaceId/integrations/triggers',
          } as const;
          void navigate({
            to: to[action],
            params: { workspaceId },
            search: { create: true },
          });
          return;
        }
        case 'add-workflow':
          // Ready workflows to install, the shortest way to a new one.
          if (workspaceId !== undefined) {
            void navigate({
              to: '/workspaces/$workspaceId/library',
              params: { workspaceId },
            });
          }
          return;
        case 'upload-file':
          if (workspaceId !== undefined) {
            void navigate({
              to: '/workspaces/$workspaceId/files',
              params: { workspaceId },
            });
          }
          return;
        case 'shortcuts':
          sheet.setOpen(true);
          return;
        // The open drawer holds the task and runs these exactly as its own
        // controls would; the palette only says which. Both guards hold by
        // construction: these entries exist only for an open task, and each
        // carries its step as `arg`.
        case 'task-start':
        case 'task-decide':
          if (openTaskId !== undefined && arg !== undefined) {
            requestTaskCommand(openTaskId, nextTaskCommand(action, arg));
          }
          return;
        case 'task-start-children':
        case 'task-stop':
        case 'task-run-again':
        case 'task-hide':
        case 'task-copy-link':
          if (openTaskId !== undefined) {
            requestTaskCommand(openTaskId, TASK_COMMAND[action]);
          }
          return;
      }
    },
    [
      boardWorkflowKey,
      navigate,
      openTaskId,
      requestTaskCommand,
      setTheme,
      sheet,
      theme,
      workspaceId,
    ],
  );

  return (
    <>
      <CommandPalette
        open={palette.open}
        onOpenChange={palette.setOpen}
        sources={sources}
        onAction={onAction}
      />
      <ShortcutSheet open={sheet.open} onOpenChange={sheet.setOpen} />
    </>
  );
}
